import "./assets/main.css";
import "./assets/PrivacyPolicy.css";
import "./assets/TermsandConditions.css";
import "./style.css";

import { createApp } from "vue";
import { createPinia } from "pinia";

import App from "./App.vue";
import router from "./router";
import { initializeCometChat } from "@/services/cometchat";
import { logWarn, logDebug, logError, logInfo } from "@/utils/secureLogger";
import { initializeCLSOptimization } from "@/utils/clsOptimization";
import { setupMobileViewport } from "@/utils/mobileViewport";

// Type declaration for Vue DevTools
declare global {
  interface Window {
    __VUE_DEVTOOLS_GLOBAL_HOOK__?: {
      enabled: boolean;
    };
    testOptimizations?: () => void;
  }
}

// Global error handlers for network and Firestore errors
if (typeof window !== "undefined") {
  // Handle unhandled promise rejections (including Firestore listener errors)
  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason;
    const errorMessage = error?.message || String(error);
    const errorCode = error?.code || "";

    // ✅ CRITICAL FIX: Suppress Firestore permission-denied errors
    // These errors occur when snapshot listeners try to read documents
    // the user doesn't have permission to access. They don't break functionality
    // because the queries are filtered properly. Suppressing these prevents
    // them from blocking component mounting and navigation.
    if (
      errorMessage.includes("permission-denied") ||
      errorMessage.includes("Missing or insufficient permissions") ||
      errorCode === "permission-denied"
    ) {
      event.preventDefault();

      logWarn("🔒 Firestore permission denied (non-critical, handled)", {
        error: errorMessage.substring(0, 100),
        code: errorCode,
        note: "Snapshot listener tried to access restricted document",
      });

      return; // Stop here, don't process further
    }

    // Check if it's a Firestore/network error
    if (
      errorMessage.includes("ERR_QUIC_PROTOCOL_ERROR") ||
      errorMessage.includes("QUIC_TOO_MANY_RTOS") ||
      errorMessage.includes("net::ERR_") ||
      errorMessage.includes("firestore")
    ) {
      // Prevent error from appearing in console (it's handled)
      event.preventDefault();

      logWarn("🔌 Network/Firestore connection issue detected (handled)", {
        error: errorMessage.substring(0, 100),
        type: "QUIC_PROTOCOL_ERROR",
      });

      // Firestore automatically reconnects, no action needed
      // Just log it for monitoring purposes
    } else {
      // For other unhandled rejections, log them
      logError("Unhandled promise rejection", {
        error: errorMessage,
        reason: error,
      });
    }
  });

  // Handle general errors
  window.addEventListener("error", (event) => {
    const error = event.error;
    const errorMessage = error?.message || event.message || String(error);

    // Filter out network errors that are handled by Firestore
    if (
      errorMessage.includes("ERR_QUIC_PROTOCOL_ERROR") ||
      errorMessage.includes("Failed to load resource") ||
      errorMessage.includes("firestore.googleapis.com")
    ) {
      event.preventDefault();

      logDebug("Network resource loading issue (handled)", {
        message: errorMessage.substring(0, 100),
      });
      return;
    }

    // Log other errors
    if (!errorMessage.includes("ResizeObserver") && !errorMessage.includes("Script error")) {
      logError("Global error caught", {
        message: errorMessage,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    }
  });

  logInfo("🛡️ Global error handlers initialized");

  // ✅ CRITICAL FIX: Intercept console.error to suppress Firebase permission-denied logs
  // Firebase's internal logger uses console.error for snapshot listener errors
  // These errors don't break functionality but flood the console and confuse users
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    const errorMsg = args.join(" ");

    // Suppress Firestore permission-denied errors from snapshot listeners
    if (
      errorMsg.includes("permission-denied") ||
      errorMsg.includes("Missing or insufficient permissions") ||
      errorMsg.includes("Uncaught Error in snapshot listener")
    ) {
      // Log as warning instead (less alarming)
      console.warn(
        "⚠️ Firestore permission issue (non-critical, handled):",
        errorMsg.substring(0, 150) + "..."
      );
      return;
    }

    // Pass through other errors normally
    originalConsoleError.apply(console, args);
  };
}

const app = createApp(App);

// Enable Vue DevTools in development
if (import.meta.env.DEV) {
  app.config.performance = true;
  if (typeof window !== "undefined" && window.__VUE_DEVTOOLS_GLOBAL_HOOK__) {
    window.__VUE_DEVTOOLS_GLOBAL_HOOK__.enabled = true;
  }
}

app.use(createPinia());
app.use(router);

// Detect WebP support and add class to html element
if (typeof window !== "undefined") {
  const webpTest = new Image();
  webpTest.onload = webpTest.onerror = function () {
    if (webpTest.height === 2) {
      document.documentElement.classList.add("webp");
    } else {
      document.documentElement.classList.add("no-webp");
    }
  };
  webpTest.src =
    "data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA";
}

// Add passive event listeners for better scroll performance
if (typeof window !== "undefined") {
  // Override addEventListener to make touch and wheel events passive by default
  const addEventListenerOriginal = EventTarget.prototype.addEventListener;
  EventTarget.prototype.addEventListener = function (type: string, listener: any, options?: any) {
    const passiveEvents = ["touchstart", "touchmove", "wheel", "mousewheel"];
    if (passiveEvents.includes(type)) {
      if (typeof options === "object") {
        options.passive = options.passive !== false; // Default to true unless explicitly false
      } else if (typeof options === "undefined") {
        options = { passive: true };
      }
    }
    return addEventListenerOriginal.call(this, type, listener, options);
  };
}

// Register service worker for better caching (production only)
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", async () => {
    try {
      const registration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
      });

      logDebug("Service Worker registered successfully", {
        scope: registration.scope,
      });

      // Handle service worker updates
      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              logDebug("New service worker available");
              // Optionally show update notification to user
            }
          });
        }
      });
    } catch (error) {
      logError("Service Worker registration failed", error as Error);
    }
  });
}

app.mount("#app");

// Initialize CLS optimization (development only)
initializeCLSOptimization();

// Initialize mobile viewport handling for virtual keyboard compatibility
setupMobileViewport();

// Initialize CometChat (app-level); login is handled per view
const appId = import.meta.env.VITE_COMETCHAT_APP_ID;
const region = import.meta.env.VITE_COMETCHAT_REGION;
const authKey = import.meta.env.VITE_COMETCHAT_AUTH_KEY;
if (appId && region && authKey) {
  initializeCometChat({ appId, region, authKey })
    .then(() => {
      logDebug("CometChat initialized successfully at app level");
    })
    .catch((e) => {
      logWarn("CometChat init failed at app level", e);
    });
} else {
  logWarn("CometChat environment variables missing", {
    appId: !!appId,
    region: !!region,
    authKey: !!authKey,
  });
}

// ✅ Register optimization diagnostics for browser testing (dev mode only)
// Disabled to avoid CSP issues - can be manually imported in console if needed
// if (import.meta.env.DEV && typeof window !== 'undefined') {
//   setTimeout(() => {
//     import("./tests/optimizationDiagnostic").then(({ runOptimizationDiagnostics }) => {
//       window.testOptimizations = runOptimizationDiagnostics;
//       logDebug("Optimization diagnostics available: window.testOptimizations()");
//     }).catch((error) => {
//       logWarn("Failed to load optimization diagnostics", error);
//     });
//   }, 1000);
// }
